import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PortalNav } from "@/components/PortalNav";
import { ShoppingCart, Plus, Loader2, X, Trash2, Package, Download, Building2 } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PO_STATUSES = ["Open", "Ordered", "Received"];
const statusStyle = (s) => s === "Received" ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/40" : s === "Ordered" ? "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/40" : "bg-zinc-100 text-zinc-500 border-zinc-300";

export default function PurchasingPortal() {
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showSuppliers, setShowSuppliers] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, iRes, sRes] = await Promise.all([
        api.get("/purchasing/orders"),
        api.get("/sales/inventory"),
        api.get("/purchasing/suppliers"),
      ]);
      setOrders(oRes.data.orders || []);
      setInventory(iRes.data.items || []);
      setSuppliers(sRes.data.suppliers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load purchasing");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (o) => {
    if (!window.confirm(`Delete ${o.number}?`)) return;
    try {
      await api.delete(`/purchasing/orders/${o.id}`);
      toast.success("Purchase order deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const changeStatus = async (o, status) => {
    if (status === o.status) return;
    if (status === "Received" && !o.received_at && !window.confirm(`Mark ${o.number} as Received? Any lines linked to a Parts Inventory item will be added to stock on hand.`)) return;
    try {
      const { data } = await api.patch(`/purchasing/orders/${o.id}/status`, { status });
      toast.success(`${o.number} → ${status}${data.restocked_lines ? ` · ${data.restocked_lines} item(s) restocked` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1"><ShoppingCart className="h-5 w-5 text-[#D4AF37]" /><span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Service Logic</span></div>
            <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Purchasing</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSuppliers(true)} data-testid="purchasing-suppliers-btn" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-4 py-3 text-sm hover:bg-zinc-100 transition-colors">
              <Building2 className="h-4 w-4" /> Suppliers
            </button>
            <button onClick={() => setShowNew(true)} data-testid="purchasing-new-btn" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-3 font-medium hover:bg-[#1E293B] transition-colors">
              <Plus className="h-4 w-4" /> New Purchase Order
            </button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
            <h2 className="font-display font-bold text-[#0F172A]">Purchase Orders</h2>
            <span className="text-xs text-zinc-400">({orders.length})</span>
          </div>
          {loading ? (
            <div className="p-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center text-zinc-400 text-sm" data-testid="purchasing-empty">No purchase orders yet. Click "New Purchase Order" to create one.</div>
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="purchasing-list">
              {orders.map((o) => (
                <div key={o.id} data-testid={`po-row-${o.id}`} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-[#0F172A] flex items-center gap-2">
                      <span className="font-mono text-sm">{o.number}</span>
                      <span className="text-zinc-500">{o.supplier || "—"}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      {(o.lines || []).length} line(s){o.expected_date ? ` · expected ${(o.expected_date || "").slice(0, 10)}` : ""}{o.created_by ? ` · by ${o.created_by}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <select value={o.status || "Open"} onChange={(e) => changeStatus(o, e.target.value)} data-testid={`po-status-${o.id}`} className={`text-[11px] uppercase tracking-wide border px-2 py-1 outline-none focus:ring-2 focus:ring-[#D4AF37] ${statusStyle(o.status)}`}>
                      {PO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span className="font-display font-bold text-[#0F172A] w-24 text-right">{money(o.total)}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/purchasing/orders/${o.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`po-pdf-${o.id}`} title="PDF" className="text-zinc-400 hover:text-[#D4AF37] transition-colors"><Download className="h-4 w-4" /></a>
                    <button onClick={() => remove(o)} data-testid={`po-del-${o.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showNew && <NewPOModal inventory={inventory} suppliers={suppliers} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {showSuppliers && <SuppliersModal suppliers={suppliers} onClose={() => setShowSuppliers(false)} onChange={load} />}
    </div>
  );
}

function SuppliersModal({ suppliers, onClose, onChange }) {
  const [form, setForm] = useState({ name: "", contact: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post("/purchasing/suppliers", form);
      setForm({ name: "", contact: "", email: "", phone: "" });
      toast.success("Supplier added");
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/purchasing/suppliers/${s.id}`); toast.success("Supplier deleted"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="suppliers-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display font-bold text-[#0F172A]">Suppliers</h2>
          <button onClick={onClose} data-testid="suppliers-close" className="text-zinc-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" placeholder="Supplier name" className="px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="supplier-contact" placeholder="Contact" className="px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" placeholder="Email" className="px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone" placeholder="Phone" className="px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>
          <button onClick={add} disabled={saving} data-testid="supplier-add" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add supplier</>}
          </button>
          <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-64 overflow-auto" data-testid="suppliers-list">
            {suppliers.length === 0 ? <div className="p-6 text-center text-zinc-400 text-sm">No suppliers yet.</div> :
              suppliers.map((s) => (
                <div key={s.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0F172A] truncate">{s.name}</div>
                    <div className="text-xs text-zinc-400 truncate">{[s.contact, s.email, s.phone].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <button onClick={() => remove(s)} data-testid={`supplier-del-${s.id}`} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewPOModal({ inventory, suppliers, onClose, onCreated }) {
  const [supplier, setSupplier] = useState("");
  const [expected, setExpected] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ uid: "l0", item_id: "", name: "", sku: "", qty: 1, unit_cost: 0 }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines((ls) => [...ls, { uid: Math.random().toString(36).slice(2), item_id: "", name: "", sku: "", qty: 1, unit_cost: 0 }]);
  const rmLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const updLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const pickItem = (i, itemId) => {
    const it = inventory.find((x) => x.id === itemId);
    updLine(i, it ? { item_id: it.id, name: it.name, sku: it.sku || "", unit_cost: it.unit_price || 0 } : { item_id: "", name: "", sku: "" });
  };

  const total = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0);

  const create = async () => {
    const clean = lines.filter((l) => l.item_id || (l.name || "").trim());
    if (clean.length === 0) { toast.error("Add at least one part"); return; }
    setSaving(true);
    try {
      await api.post("/purchasing/orders", {
        supplier: supplier.trim(),
        expected_date: expected || null,
        notes,
        lines: clean.map((l) => ({ item_id: l.item_id || null, name: l.name, sku: l.sku || "", qty: parseFloat(l.qty) || 0, unit_cost: parseFloat(l.unit_cost) || 0 })),
      });
      toast.success("Purchase order created");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="po-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-display font-bold text-[#0F172A]">New Purchase Order</h2>
          <button onClick={onClose} data-testid="po-close" className="text-zinc-400 hover:text-[#0F172A]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Supplier / Vendor</label>
              <input value={supplier} onChange={(e) => setSupplier(e.target.value)} list="po-supplier-list" data-testid="po-supplier" placeholder="Type or pick a supplier" className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <datalist id="po-supplier-list">{suppliers.map((s) => <option key={s.id} value={s.name} />)}</datalist>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Expected Date</label>
              <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="po-expected" className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="po-notes" className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-[#0F172A]">Parts to Purchase</h3>
              <button onClick={addLine} data-testid="po-add-line" className="flex items-center gap-1.5 border border-zinc-300 text-[#0F172A] px-3 py-1.5 text-sm hover:bg-zinc-50 transition-colors"><Plus className="h-4 w-4" /> Add Line</button>
            </div>
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              <div className="grid grid-cols-[1fr_90px_110px_36px] gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wide text-zinc-500">
                <span>Inventory Item</span><span>Quantity</span><span>Unit Cost</span><span></span>
              </div>
              {lines.map((l, i) => (
                <div key={l.uid} className="grid grid-cols-[1fr_90px_110px_36px] gap-2 px-3 py-2 items-center" data-testid={`po-line-${i}`}>
                  {inventory.length > 0 ? (
                    <select value={l.item_id} onChange={(e) => pickItem(i, e.target.value)} data-testid={`po-line-item-${i}`} className="px-2 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                      <option value="">Select part…</option>
                      {inventory.map((it) => <option key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ""}</option>)}
                    </select>
                  ) : (
                    <input value={l.name} onChange={(e) => updLine(i, { name: e.target.value })} data-testid={`po-line-name-${i}`} placeholder="Part name" className="px-2 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                  )}
                  <input type="number" min="0" value={l.qty} onChange={(e) => updLine(i, { qty: e.target.value })} data-testid={`po-line-qty-${i}`} className="px-2 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                  <input type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => updLine(i, { unit_cost: e.target.value })} data-testid={`po-line-cost-${i}`} className="px-2 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                  <button onClick={() => rmLine(i)} data-testid={`po-line-remove-${i}`} className="text-zinc-400 hover:text-[#EF4444] flex justify-center"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3 text-[#0F172A] font-display font-bold" data-testid="po-total">Total: {money(total)}</div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200">
          <button onClick={create} disabled={saving} data-testid="po-create" className="w-full flex items-center justify-center gap-2 bg-[#7A1420] text-white py-3 font-medium hover:bg-[#651019] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Package className="h-4 w-4" /> Create Purchase Order</>}
          </button>
        </div>
      </div>
    </div>
  );
}
